import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const fmt = (s) => {
  if (!s || s < 0) return "0:00";
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = Math.floor(s % 60);
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}` : `${m}:${String(sec).padStart(2, "0")}`;
};

export default function StreamPlayer() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, loading } = useAuth();
  const [meta, setMeta] = useState(null);
  const [ci, setCi] = useState(0);
  const [resumeAsked, setResumeAsked] = useState(false);
  const mediaRef = useRef(null);
  const lastSaved = useRef(0);

  useEffect(() => { if (!loading && !user) nav("/login"); }, [loading, user, nav]);

  useEffect(() => {
    if (!user) return;
    api.get(`/streams/${id}`).then(({ data }) => {
      if (!data.entitled) {
        toast.error("Buy this stream first to unlock full playback");
        nav(`/product/${id}`);
        return;
      }
      setMeta(data);
    }).catch(() => nav("/"));
  }, [id, user, nav]);

  // Resume dialog: only once, after first load
  useEffect(() => {
    if (!meta || resumeAsked) return;
    const p = meta.progress;
    if (p && p.position_seconds > 5) {
      setResumeAsked(true);
      const ok = window.confirm(
        `You left off in "${meta.chapters[p.chapter_index]?.title || `Chapter ${p.chapter_index + 1}`}" at ${fmt(p.position_seconds)}.\n\nOK = resume · Cancel = start over`
      );
      if (ok) {
        setCi(p.chapter_index);
        setTimeout(() => {
          if (mediaRef.current) {
            mediaRef.current.currentTime = p.position_seconds;
            mediaRef.current.play().catch(() => {});
          }
        }, 500);
      } else {
        setCi(0);
      }
    } else {
      setResumeAsked(true);
    }
  }, [meta, resumeAsked]);

  const mediaSrc = useMemo(() => {
    if (!meta) return "";
    const token = localStorage.getItem("token");
    return `${BACKEND_URL}/api/streams/${id}/media/${ci}?t=${token || ""}`;
  }, [meta, id, ci]);

  // Auth header via `fetch` doesn't work with native <video>/<audio> src, so we set the header via a helper.
  // Simplest: pass token as query and let backend read via ?token if present. Since we already require Bearer,
  // we set Authorization by pointing src to a blob is heavy — instead, we send credentials via cookie or fall back.
  // Cleanest fix: also accept token query param on the streaming endpoint. Frontend uses cookie/token; here we use Bearer via a helper URL.
  // For simplicity in MVP we register a service-worker-less path: the endpoint accepts session cookies (already available).
  // We'll rely on session_token cookie set by the backend at login.

  const isVideo = meta?.stream_type === "video";
  const chapter = meta?.chapters?.[ci];

  const saveProgress = () => {
    if (!mediaRef.current || !meta) return;
    const now = Date.now();
    if (now - lastSaved.current < 9000) return; // throttle: every ~10s
    lastSaved.current = now;
    const pos = mediaRef.current.currentTime;
    api.post(`/streams/${id}/progress`, { chapter_index: ci, position_seconds: pos }).catch(() => {});
  };

  const selectChapter = (i) => {
    if (mediaRef.current) {
      api.post(`/streams/${id}/progress`, { chapter_index: ci, position_seconds: mediaRef.current.currentTime }).catch(() => {});
    }
    setCi(i);
    setTimeout(() => { if (mediaRef.current) { mediaRef.current.currentTime = 0; mediaRef.current.play().catch(() => {}); } }, 200);
  };

  const restartFromStart = () => {
    setCi(0);
    setTimeout(() => { if (mediaRef.current) { mediaRef.current.currentTime = 0; mediaRef.current.play().catch(() => {}); } }, 200);
    api.post(`/streams/${id}/progress`, { chapter_index: 0, position_seconds: 0 }).catch(() => {});
  };

  if (loading || !meta) return <Layout><div className="max-w-[1200px] mx-auto px-6 py-24 mono uppercase text-sm tracking-[0.2em]">Loading player…</div></Layout>;

  return (
    <Layout>
      <section className="max-w-[1200px] mx-auto px-6 py-10" data-testid="stream-player-page">
        <Link to={`/product/${id}`} className="mono text-xs uppercase tracking-[0.25em] text-[#555] hover:text-[#C77E3C]">← Product page</Link>
        <div className="mt-3 flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C]">/ {meta.stream_type}</div>
            <h1 data-testid="stream-title-heading" className="heading text-4xl sm:text-5xl font-black uppercase tracking-tighter mt-1">{meta.title}</h1>
            <div className="mono text-[10px] uppercase tracking-[0.25em] text-[#555] mt-2">by {meta.seller_name} · {fmt(meta.total_duration_seconds)}</div>
          </div>
          <div className="brut-border bg-[#FDF3E9] px-3 py-2 mono text-[10px] uppercase tracking-[0.25em] text-[#1F3D2A]">Buy once — stream for life</div>
        </div>

        <div className="mt-6 brut-border bg-black overflow-hidden" data-testid="stream-media-frame">
          {isVideo ? (
            <video
              key={`v-${ci}`}
              ref={mediaRef}
              data-testid="stream-video"
              controls
              controlsList="nodownload noremoteplayback"
              disablePictureInPicture
              onContextMenu={(e) => e.preventDefault()}
              onTimeUpdate={saveProgress}
              onPause={saveProgress}
              onEnded={() => { if (ci + 1 < meta.chapters.length) selectChapter(ci + 1); }}
              className="w-full aspect-video bg-black"
              src={mediaSrc}
            />
          ) : (
            <div className="p-8 bg-[#1F3D2A] text-white">
              <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C]">NOW STREAMING</div>
              <div className="heading text-2xl font-bold mt-2">{chapter?.title}</div>
              <div className="mono text-[10px] uppercase tracking-[0.25em] text-white/60 mt-1">Chapter {ci + 1} of {meta.chapters.length} · {fmt(chapter?.duration_seconds)}</div>
              <audio
                key={`a-${ci}`}
                ref={mediaRef}
                data-testid="stream-audio"
                controls
                controlsList="nodownload noremoteplayback"
                onContextMenu={(e) => e.preventDefault()}
                onTimeUpdate={saveProgress}
                onPause={saveProgress}
                onEnded={() => { if (ci + 1 < meta.chapters.length) selectChapter(ci + 1); }}
                className="w-full mt-6"
                src={mediaSrc}
              />
            </div>
          )}
        </div>

        <div className="mt-4 flex flex-wrap gap-3">
          <button data-testid="restart-stream" onClick={restartFromStart} className="brut-border bg-white px-4 py-2 mono text-xs uppercase tracking-[0.25em] hover:bg-[#FDF3E9]">↺ Start from Chapter 1</button>
          <div className="brut-border bg-white px-4 py-2 mono text-xs uppercase tracking-[0.25em] text-[#555]">Progress auto-saves — leave anytime, we&apos;ll pick up where you stopped</div>
        </div>

        <div className="mt-8" data-testid="stream-chapter-tabs">
          <h3 className="heading text-2xl font-bold uppercase">Chapters</h3>
          <div className="mt-3 brut-border bg-white">
            {meta.chapters.map((c, i) => (
              <button key={i} data-testid={`chapter-tab-${i}`} onClick={() => selectChapter(i)}
                className={`w-full text-left p-4 flex items-center gap-4 hover:bg-[#FDF3E9] ${i > 0 ? "border-t-2 border-[#1F3D2A]" : ""} ${i === ci ? "bg-[#FDF3E9]" : ""}`}>
                <div className={`mono text-[10px] uppercase tracking-[0.3em] w-10 ${i === ci ? "text-[#C77E3C] font-bold" : "text-[#555]"}`}>{String(i + 1).padStart(2, "0")}</div>
                <div className="flex-1">
                  <div className="heading font-bold">{c.title}</div>
                  {i === ci && <div className="mono text-[9px] uppercase tracking-[0.25em] text-[#C77E3C] mt-1">▶ Playing</div>}
                </div>
                <div className="mono text-[10px] uppercase tracking-[0.25em] text-[#555]">{fmt(c.duration_seconds)}</div>
              </button>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
